/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Box, Button, Theme } from '@nexigendigital/nxui'
import { useCreateCartMutation, useUpdateCartItemsMutation } from 'api/shop'
import { clearItems, setCartOpen } from 'store/slices/shopSlice'
import { Cart, PendingItem } from 'models/shop'
import { ProductServiceType } from 'models/enums'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { removePropertyByKeys } from 'helpers/utils'

import CartSummary from 'containers/shop/cart/cartSummary'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface CartButtonProps {
    cart: null | Cart,
    updatedItems: object[] | PendingItem[],
    isHostingHasName: boolean
}

interface FooterProps {
    justifyContent: boolean
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function MiniCart() {
    const dispatch = useAppDispatch()
    const { items: pendingItems, cart } = useAppSelector((state) => state.shop)
    const [updateCartItems] = useUpdateCartItemsMutation()
    const updatedItems = pendingItems.map((item) => {
        return removePropertyByKeys(item, ['pricing_list', 'product_type', 'pricing', 'eligibility_fields'])
    })

    function isHostingHasName() {
        return pendingItems.every((pendingItem: PendingItem) => {
            if (pendingItem.product_type === ProductServiceType.SharedHosting) {
                return pendingItem.name && pendingItem.name !== undefined
            }

            return true
        })
    }

    return (
        <StyledSummary>
            <Box header={{ title: 'Order Total' }}>
                <CartSummary/>
                <Footer justifyContent={pendingItems.length >= 1}>
                    {pendingItems.length >= 1 &&
                        (
                            <Anchor color={'error'} onClick={() => {
                                if (cart && cart.uuid) {
                                    updateCartItems({
                                        uuid: cart.uuid,
                                        remove_items: cart.items.map(({ item_uuid }) => item_uuid)
                                    })
                                }
                                dispatch(clearItems())
                            }}>
                                Clear Selections
                            </Anchor>
                        )
                    }
                    <CartButton
                        cart={cart}
                        updatedItems={updatedItems}
                        isHostingHasName={isHostingHasName()}
                    />
                </Footer>
            </Box>
        </StyledSummary>
    )
}

function CartButton({ cart, updatedItems, isHostingHasName }: CartButtonProps) {
    const dispatch = useDispatch()
    const { cartUpdateStatus } = useAppSelector((state) => state.shop)
    const [createCart] = useCreateCartMutation()
    const [updateCartItems, { isSuccess }] = useUpdateCartItemsMutation()
    const itemsNotInCart = updatedItems.filter((updatedItem: any) => {
        return Boolean(!cart?.items.find((cartItem) => {
            return cartItem.name === updatedItem.name && cartItem.product_id === updatedItem.product_id
        }))
    })

    useEffect(() => {
        if (isSuccess) {
            dispatch(setCartOpen(true))
        }
    }, [isSuccess])

    if (cart?.uuid) {
        return (
            <Button
                type={'button'}
                color={'primary'}
                disabled={cartUpdateStatus === 'loading'}
                onClick={() => {
                    if (itemsNotInCart.length >= 1) {
                        updateCartItems({ uuid: cart.uuid, items: itemsNotInCart })
                    } else {
                        dispatch(setCartOpen(true))
                    }
                }}
            >
                View Cart
            </Button>
        )
    }

    return (
        <Button
            type={'button'}
            color={'primary'}
            disabled={!(updatedItems.length >= 1 && isHostingHasName)}
            onClick={() => {
                createCart({ items: updatedItems })
                dispatch(setCartOpen(true))
            }}
        >
            Add to Cart
        </Button>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const StyledSummary = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;

    > div {
        ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
            min-width: initial;
            width: 100%;
        }

        > div {
            > div {
                border: none;
                border-radius: 0;
            }
        }
    }
`

const Footer = styled.div<FooterProps>`
    width: 100%;
    padding: 12px 24px;
    display: flex;
    flex-direction: row;
    align-items: center;
    border-top: 1px solid ${props => props.theme['gray--100']};
    justify-content: ${props => props.justifyContent ? 'space-between' : 'flex-end'};
`
